/**
 * Per-document page id for the anonymous INP report (RFC-0974 follow-up, §5.6).
 *
 * `page_id` is "one id per document lifecycle" — a session spans multiple
 * documents (reloads / full-page OAuth navigations each start a new document).
 * It is therefore an IN-MEMORY module constant, NOT sessionStorage: a reload
 * creates a fresh module scope and thus a fresh `page_id`, while the session_id
 * (sessionStorage) persists across that reload. The platform selects the latest
 * INP per `session_id + page_id`, so a stable-per-document id is exactly right.
 */

let pageId: string | null = null

function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through to the manual generator */
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/** The id for this document lifecycle (created lazily, stable until reload). */
export function getPageId(): string {
  if (pageId === null) pageId = newId()
  return pageId
}

/** Test helper — reset the in-memory page id. */
export function _resetPageIdForTest(): void {
  pageId = null
}
